import React, { useState, useEffect } from 'react';
import { Bell, BellOff, X, Check } from 'lucide-react';
import { API_URL } from '../services/apiConfig';
import { 
  requestNotificationPermission, 
  saveFCMTokenToBackend,
  onForegroundMessage 
} from '../lib/firebase';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

interface NotificationPromptProps {
  userId?: string;
  language?: string;
  onClose?: () => void;
}

export const NotificationPrompt: React.FC<NotificationPromptProps> = ({ 
  userId, 
  language = 'hi',
  onClose 
}) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermission | 'unsupported'>('default');
  const [isLoading, setIsLoading] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);

  // Detect Capacitor native using top-level import
  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    if (isNative) {
      // In Capacitor, always supported
      const savedToken = localStorage.getItem('fcm_token');
      if (savedToken) {
        setIsEnabled(true);
        setPermissionStatus('granted');
      } else {
        // Show prompt for native app too
        const timer = setTimeout(() => {
          const dismissed = localStorage.getItem('notification_prompt_dismissed');
          if (!dismissed || Date.now() - parseInt(dismissed) > 7 * 24 * 60 * 60 * 1000) {
            setShowPrompt(true);
          }
        }, 3000);
        return () => clearTimeout(timer);
      }
    } else {
      // Web: Check if notifications are supported
      if (!('Notification' in window)) {
        setPermissionStatus('unsupported');
        return;
      }

      setPermissionStatus(Notification.permission);
      
      const savedToken = localStorage.getItem('fcm_token');
      if (savedToken && Notification.permission === 'granted') {
        setIsEnabled(true);
      }

      if (Notification.permission === 'default') {
        const timer = setTimeout(() => {
          const dismissed = localStorage.getItem('notification_prompt_dismissed');
          if (!dismissed || Date.now() - parseInt(dismissed) > 7 * 24 * 60 * 60 * 1000) {
            setShowPrompt(true);
          }
        }, 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [isNative]);

  // Listen for foreground messages (web only)
  useEffect(() => {
    if (isNative || permissionStatus !== 'granted') return;

    const unsubscribe = onForegroundMessage((payload) => {
      const title = payload.notification?.title || 'नया संदेश';
      const body = payload.notification?.body || '';
      
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(title, {
          body,
          icon: '/logo192.png',
          badge: '/logo192.png'
        });
      }
    });

    return () => unsubscribe();
  }, [permissionStatus, isNative]);

  const handleEnable = async () => {
    setIsLoading(true);
    
    try {
      if (isNative) {
        // Capacitor native push
        // PushNotifications imported at top level
        const permResult = await PushNotifications.requestPermissions();
        
        if (permResult.receive === 'granted') {
          // Attach listeners BEFORE register to avoid race condition
          PushNotifications.addListener('registration', async (token) => {
            localStorage.setItem('fcm_token', token.value);
                      await fetch(`${API_URL}/api/notifications/register-token`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ fcm_token: token.value, user_id: userId, platform: 'android', device_type: 'capacitor' }),
            });
            setIsEnabled(true);
            setPermissionStatus('granted');
            setShowPrompt(false);
          });
          
          PushNotifications.addListener('registrationError', (error) => {
            console.error('Native push registration error:', error);
          });

          await PushNotifications.register();
        } else {
          setPermissionStatus('denied');
        }
      } else {
        // Web push (existing)
        const token = await requestNotificationPermission();
        
        if (token) {
          localStorage.setItem('fcm_token', token);
          await saveFCMTokenToBackend(token, userId);
          setIsEnabled(true);
          setPermissionStatus('granted');
          setShowPrompt(false);
        } else {
          setPermissionStatus(Notification.permission);
        }
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('notification_prompt_dismissed', Date.now().toString());
    setShowPrompt(false);
    if (onClose) onClose();
  };

  // Don't render if notifications not supported
  if (permissionStatus === 'unsupported') {
    return null;
  }

  // Compact toggle for settings/header
  if (!showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 animate-slide-up">
      <div className="bg-white dark:bg-stone-800 rounded-2xl shadow-2xl border border-orange-200 dark:border-stone-700 p-4 max-w-sm mx-auto">
        <button
          onClick={handleDismiss}
          className="absolute -top-2 -right-2 w-7 h-7 bg-gray-200 dark:bg-stone-600 rounded-full flex items-center justify-center shadow-md"
        >
          <X size={14} />
        </button>

        <div className="flex items-start gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-saffron to-orange-500 rounded-xl flex items-center justify-center shrink-0">
            <Bell size={24} className="text-white" />
          </div>
          
          <div className="flex-1">
            <h3 className="font-bold text-gray-800 dark:text-white text-sm">
              {language === 'hi' ? '🔔 सूचनाएं चालू करें' : '🔔 Enable Notifications'}
            </h3>
            <p className="text-xs text-gray-500 dark:text-stone-400 mt-1">
              {language === 'hi' 
                ? 'रोज़ सुबह श्लोक और एकादशी की याद पाएं'
                : 'Get daily Shloka and Ekadashi reminders'}
            </p>
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <button
            onClick={handleDismiss}
            className="flex-1 py-2 px-3 bg-gray-100 dark:bg-stone-700 text-gray-600 dark:text-stone-300 rounded-xl text-xs font-bold"
          >
            {language === 'hi' ? 'बाद में' : 'Later'}
          </button>
          <button
            onClick={handleEnable}
            disabled={isLoading}
            className="flex-1 py-2 px-3 bg-gradient-to-r from-saffron to-orange-500 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1"
          >
            {isLoading ? (
              <span className="animate-spin">⏳</span>
            ) : (
              <>
                <Check size={14} />
                {language === 'hi' ? 'हां, चालू करें' : 'Yes, Enable'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Notification Toggle Button for Settings
export const NotificationToggle: React.FC<{ userId?: string; language?: string }> = ({ 
  userId,
  language = 'hi' 
}) => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Detect Capacitor
  const isNativeNotif = (() => {
    try { const { Capacitor } = require('@capacitor/core'); return Capacitor.isNativePlatform(); } catch { return false; }
  })();

  useEffect(() => {
    const savedToken = localStorage.getItem('fcm_token');
    if (isNativeNotif) {
      setIsEnabled(!!savedToken);
    } else if ('Notification' in window) {
      setIsEnabled(!!savedToken && Notification.permission === 'granted');
    }
  }, [isNativeNotif]);

  const handleToggle = async () => {
    if (isEnabled) {
      localStorage.removeItem('fcm_token');
      setIsEnabled(false);
    } else {
      setIsLoading(true);
      if (isNativeNotif) {
        const { requestNativeNotificationPermission } = await import('../services/notificationHelper');
        const success = await requestNativeNotificationPermission();
        setIsEnabled(success);
      } else {
        const token = await requestNotificationPermission();
        if (token) {
          localStorage.setItem('fcm_token', token);
          await saveFCMTokenToBackend(token, userId);
          setIsEnabled(true);
        }
      }
      setIsLoading(false);
    }
  };

  // Always show in Capacitor, check browser support for web
  if (!isNativeNotif && !('Notification' in window)) {
    return null;
  }

  return (
    <button
      onClick={handleToggle}
      disabled={isLoading}
      className={`flex items-center gap-3 w-full p-4 rounded-2xl transition-all ${
        isEnabled 
          ? 'bg-green-50 dark:bg-green-900/20 border-2 border-green-500' 
          : 'bg-gray-50 dark:bg-stone-800 border-2 border-transparent'
      }`}
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
        isEnabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-stone-600'
      }`}>
        {isEnabled ? <Bell size={20} className="text-white" /> : <BellOff size={20} className="text-white" />}
      </div>
      <div className="flex-1 text-left">
        <p className="font-bold text-sm dark:text-white">
          {language === 'hi' ? 'दैनिक सूचनाएं' : 'Daily Notifications'}
        </p>
        <p className="text-xs text-gray-500 dark:text-stone-400">
          {isEnabled 
            ? (language === 'hi' ? 'चालू है' : 'Enabled')
            : (language === 'hi' ? 'बंद है' : 'Disabled')
          }
        </p>
      </div>
      <div className={`w-12 h-7 rounded-full p-1 transition-colors ${
        isEnabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-stone-600'
      }`}>
        <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${
          isEnabled ? 'translate-x-5' : 'translate-x-0'
        }`} />
      </div>
    </button>
  );
};

export default NotificationPrompt;
